/**
 * Escritas do caso (gatilho e modalidade); ambas devolvem o detalhe ATUALIZADO, já mapeado.
 * `requisitar` traduz `!ok` em `ErroDaApi` com `.status` (400, 401, 404, 409, 422): nenhum vira sucesso silencioso.
 * O ator NÃO vai no corpo: o back o lê da sessão.
 */

import type {
  ApplyCaseTransitionRequest,
  CaseDetail,
  OpenCaseWaitRequest,
  SetCaseModalityRequest,
} from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import type { Turno } from '../../../shared/filaDaAvaliadora/acionamento';
import { mapearCasoDetalhado } from './mapeadores';
import type { CasoDetalhado, GatilhoDoCaso, ModalidadeDoCaso } from './tipos';

export function aplicarTransicao(id: string, gatilho: GatilhoDoCaso, sinal?: AbortSignal): Promise<CasoDetalhado> {
  const corpo: ApplyCaseTransitionRequest = { trigger: gatilho };
  return requisitar<CaseDetail>(`/casos/${encodeURIComponent(id)}/transicoes`, {
    method: 'POST',
    corpo,
    sinal,
  }).then(mapearCasoDetalhado);
}

/** `PUT /casos/{id}/modalidade`. Ventilação contínua só com `ID` — o back recusa com `AD`. */
export function definirModalidade(
  id: string,
  modalidade: ModalidadeDoCaso,
  ventilacaoContinua: boolean,
  sinal?: AbortSignal,
): Promise<CasoDetalhado> {
  const corpo: SetCaseModalityRequest = { modality: modalidade, continuousVentilation: ventilacaoContinua };
  return requisitar<CaseDetail>(`/casos/${encodeURIComponent(id)}/modalidade`, {
    method: 'PUT',
    corpo,
    sinal,
  }).then(mapearCasoDetalhado);
}

/**
 * `POST /casos/{id}/frentes/{frente}/passos/{ordem}` — fecha o passo da vez.
 *
 * `camposConferidos` são as CHAVES que a pessoa marcou, nunca os valores:
 * restrição 6 do `CLAUDE.md`, o clínico é do IW. O back recusa com `422` e
 * NOMEIA o que falta quando a cobertura está incompleta.
 *
 * `doMarco` só vale no passo com marco da fila da avaliadora (`TP-065`): a avaliadora anotada no passo que a aciona, e
 * o turno no passo do aceite, onde o back o exige (`400 EVALUATION_SHIFT_REQUIRED`).
 */
export function fecharPasso(
  id: string,
  frente: string,
  ordem: number,
  camposConferidos: readonly string[],
  doMarco: { readonly avaliadora?: string; readonly turno?: Turno } = {},
  sinal?: AbortSignal,
): Promise<CasoDetalhado> {
  return requisitar<CaseDetail>(
    `/casos/${encodeURIComponent(id)}/frentes/${encodeURIComponent(frente)}/passos/${ordem}`,
    {
      method: 'POST',
      corpo: { camposConferidos: [...camposConferidos], avaliadora: doMarco.avaliadora, turno: doMarco.turno },
      sinal,
    },
  ).then(mapearCasoDetalhado);
}

/**
 * `POST /casos/{id}/frentes/{frente}/dispensar` — dispensa a frente, com motivo.
 *
 * O motivo é obrigatório (`400` sem ele): sem motivo, dispensar e não fazer
 * ficam iguais no histórico.
 */
export function dispensarFrente(
  id: string,
  frente: string,
  motivo: string,
  sinal?: AbortSignal,
): Promise<CasoDetalhado> {
  return requisitar<CaseDetail>(`/casos/${encodeURIComponent(id)}/frentes/${encodeURIComponent(frente)}/dispensar`, {
    method: 'POST',
    corpo: { motivo },
    sinal,
  }).then(mapearCasoDetalhado);
}

/**
 * Abre a espera da família (`POST /casos/{id}/esperas`, `{ "kind": "Family" }`). Só a família se abre à mão — a da
 * operadora (`Payer`) é derivada do estado e pedi-la é `400`. Já aberta: `409 WAIT_ALREADY_OPEN`.
 */
export function abrirEsperaDaFamilia(id: string, sinal?: AbortSignal): Promise<CasoDetalhado> {
  const corpo: OpenCaseWaitRequest = { kind: 'Family' };
  return requisitar<CaseDetail>(`/casos/${encodeURIComponent(id)}/esperas`, {
    method: 'POST',
    corpo,
    sinal,
  }).then(mapearCasoDetalhado);
}

/**
 * Fecha uma espera aberta (`POST /casos/{id}/esperas/{waitId}/fechar`, sem corpo). Já fechada, ou de outro Caso:
 * `409 WAIT_NOT_OPEN`.
 */
export function fecharEspera(id: string, esperaId: string, sinal?: AbortSignal): Promise<CasoDetalhado> {
  return requisitar<CaseDetail>(`/casos/${encodeURIComponent(id)}/esperas/${encodeURIComponent(esperaId)}/fechar`, {
    method: 'POST',
    sinal,
  }).then(mapearCasoDetalhado);
}
